import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {Checkbox} from 'shells/checkbox/checkbox';
import {Illustration} from 'shells/illustrations/illustration';
import {Stack} from 'shells/layout/stack/stack';
import {TwoPaneModal} from 'shells/modal/two-pane-modal';
import {Body} from 'shells/typography/body';

import {useGetLimitsByType} from '../../limits';

import {useStagesetActivateDraft} from '../graphql/hooks/use-stageset-activate-draft';

import styles from './activate-draft-pipeline-confirmation-modal.module.css';

type Props = {
    onClose: () => void;
    stagesetId: string;
    stagesetName: string;
};

export function ActivateDraftPipelineConfirmationModal(props: Props) {
    const {onClose} = props;
    const [setAsDefault, setSetAsDefault] = React.useState<boolean>(false);
    const [isActivating, setIsActivating] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);

    const {stagesetActivateDraft} = useStagesetActivateDraft();
    const {limits} = useGetLimitsByType('PIPELINES');

    const isPipelineLimitOne = limits && limits.currentPlanLimit === 1;

    React.useEffect(() => {
        if (isPipelineLimitOne) {
            setSetAsDefault(true);
        }
    }, [isPipelineLimitOne]);

    const handleActivatePipeline = React.useCallback(() => {
        setIsActivating(true);
        setErrorMessage(null);
        stagesetActivateDraft({stagesetId: props.stagesetId, shouldSetAsDefault: setAsDefault})
            .then(() => {
                setIsActivating(false);
                onClose();
            })
            .catch((error) => {
                setIsActivating(false);
                setErrorMessage(safelyGetGraphQLErrorMessage(error));
            });
    }, [stagesetActivateDraft, props.stagesetId, setAsDefault, onClose]);

    return (
        <TwoPaneModal
            // @ts-expect-error
            isOpen={true}
            maxHeight={600}
            onClose={props.onClose}
            leftSection={
                <div className='flex background-xlt-blue full-width justify-center'>
                    <Illustration src='pipeline-template' />
                </div>
            }
            rightSection={
                <div className='flex align-center flex-dir-col full-height pad-16 border-box'>
                    <div className='flex flex-dir-col justify-center flex-grow'>
                        <Stack spacing={24} justifyContent='center'>
                            <Body size='xlarge'>
                                Ready to activate <i>{props.stagesetName}?</i>
                            </Body>
                            <Body>
                                You will be able to add new leads to this pipeline, and move them
                                through each stage based on your setup. You can make changes or
                                deactivate it whenever you need.
                            </Body>
                            <Checkbox
                                label='Set this pipeline as the new default'
                                checked={setAsDefault}
                                onChange={(checked) => setSetAsDefault(checked)}
                                isLarge={true}
                                disabled={isPipelineLimitOne}
                            />
                        </Stack>
                    </div>
                    <div className={styles['error-message-container']}>
                        {errorMessage && <Body color='error'>{errorMessage}</Body>}
                        {isPipelineLimitOne && !errorMessage && !isActivating && (
                            <Body color='warn'>
                                By activating this pipeline, the current active pipeline will be
                                deactivated due to your plan limit.
                            </Body>
                        )}
                    </div>
                    <Stack
                        isHorizontal={true}
                        spacing={0}
                        isFullWidth={true}
                        justifyContent='space-between'
                    >
                        <Button variant='text-secondary' onClick={props.onClose} size='big'>
                            Cancel
                        </Button>
                        <SaveButton
                            variant='primary'
                            // @ts-expect-error
                            onClick={handleActivatePipeline}
                            size='big'
                            buttonText='Activate pipeline'
                            savingText='Activating pipeline...'
                            successText='Pipeline activated'
                            isSaving={isActivating}
                            errorSaving={Boolean(errorMessage)}
                        />
                    </Stack>
                </div>
            }
        />
    );
}
